/**
 * SignatureDialog — eigen e-mailhandtekening per gebruiker (onder Frits-concepten).
 * Rich-paste: een contentEditable veld behoudt bij plakken uit Outlook de opmaak
 * én afbeeldingen (een gewoon tekstveld ontvangt alleen platte tekst — dat ging mis).
 * Opslaan = de HTML van het veld; leegmaken = terug naar de bedrijfs-default.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, CircularProgress, Alert, Chip,
} from '@mui/material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from './demoAdapter'; // demo-modus: niets naar de server

export default function SignatureDialog({ open, onClose }) {
  const editorRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [usesDefault, setUsesDefault] = useState(true);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMsg(null); setError(null); setLoading(true);
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/signature`);
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'Handtekening laden mislukt');
        setUsesDefault(!!d.uses_default);
        // Eigen handtekening tonen; bij default de effectieve (bedrijfs)handtekening als vertrekpunt.
        if (editorRef.current) editorRef.current.innerHTML = d.signature_html || d.effective_html || '';
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [open]);

  const save = async (html) => {
    setSaving(true); setError(null); setMsg(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/signature`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature_html: html }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Opslaan mislukt');
      setUsesDefault(!!d.uses_default);
      setMsg(html ? 'Opgeslagen — Frits gebruikt vanaf nu jouw handtekening ✔' : 'Teruggezet naar de bedrijfs-standaard ✔');
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        ✍️ Mijn e-mailhandtekening
        {usesDefault && <Chip size="small" label="bedrijfs-standaard actief" sx={{ ml: 1, height: 20, fontSize: 11 }} />}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Kopieer je handtekening in Outlook (Instellingen → Handtekening: alles selecteren → kopiëren) en{' '}
          <b>plak hem hieronder</b> — opmaak en afbeeldingen blijven behouden. Frits plakt dit onder elk concept,
          boven de gequote thread.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
        {msg && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setMsg(null)}>{msg}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>
        ) : (
          <Box
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            sx={{
              minHeight: 180, maxHeight: 380, overflowY: 'auto',
              p: 1.5, borderRadius: 1.5, border: '1px solid #cbd5e1', bgcolor: '#fff',
              fontSize: 14, lineHeight: 1.5, outline: 'none',
              '&:focus': { borderColor: '#526075', boxShadow: '0 0 0 2px rgba(82,96,117,0.15)' },
              '& img': { maxWidth: '100%' },
            }}
          />
        )}
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
          Tip: check na het plakken of de afbeeldingen zichtbaar zijn — dan komen ze ook goed in de mail.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => save('')} disabled={saving || loading || usesDefault} color="warning" sx={{ textTransform: 'none' }}>
          Terug naar standaard
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>Sluiten</Button>
        <Button variant="contained" disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          onClick={() => save(editorRef.current?.innerHTML || '')}
          sx={{ textTransform: 'none', fontWeight: 800 }}>
          Opslaan
        </Button>
      </DialogActions>
    </Dialog>
  );
}
